import {
  Form,
  Input,
  Button,
  Card,
  DatePicker,
  ConfigProvider,
  Modal,
  Tabs,
  Row,
  Col,
  Select,
  App,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useCreateFamily, useUpdateFamily } from "network/queries/family.query";
import {
  ICarInfo,
  IChildInfo,
  IFamily,
  IFarmlandInfo,
  IParentInfo,
} from "network/models/family.model";
import FamilyFormStepper from "./FamilyStepper/FamilyFormStepper";

type ParentInfoForm = Omit<IParentInfo, "birthdate"> & {
  birthdate?: Dayjs | null;
};
type ChildInfoForm = Omit<IChildInfo, "birthdate"> & {
  birthdate?: Dayjs | null;
};
type CarInfoForm = Omit<
  ICarInfo,
  "registrationDate" | "year" | "estimatedValue"
> & {
  registrationDate?: Dayjs | null;
  year?: number | string;
  estimatedValue?: number | string;
};

export type FamilyFormValues = {
  father: ParentInfoForm;
  mother: ParentInfoForm;
  children: ChildInfoForm[];
  cars?: CarInfoForm[];
  farmlands?: IFarmlandInfo[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  selected?: IFamily;
}

const toDayjs = (d?: Date | string | null) => (d ? dayjs(d) : null);
const toISO = (d?: Dayjs | null) => (d ? d.toISOString() : undefined);
const toNum = (v: any): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export default function FamilyForm({ open, onClose, selected }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FamilyFormValues>();
  const navigate = useNavigate();
  const id = selected?._id ?? null;
  const [rtl] = useState(true);

  const {
    mutate: createFamily,
    isLoading: createLoading,
    isSuccess: createSucceeded,
  } = useCreateFamily();
  const {
    mutate: updateFamily,
    isLoading: updateLoading,
    isSuccess: updateSucceeded,
  } = useUpdateFamily();

  useEffect(() => {
    if (!selected) {
      form.resetFields();
      return;
    }
    const data = selected;
    const initial: FamilyFormValues = {
      father: {
        ...data.father,
        birthdate: toDayjs(data.father?.birthdate as any),
      },
      mother: {
        ...data.mother,
        birthdate: toDayjs(data.mother?.birthdate as any),
      },
      children: (data.children ?? []).map((c) => ({
        ...c,
        birthdate: toDayjs(c.birthdate as any),
      })),
      cars: (data.cars ?? []).map((c) => ({
        ...c,
        registrationDate: toDayjs(c.registrationDate as any),
      })),
      farmlands: data.farmlands ?? [],
    };
    form.setFieldsValue(initial);
  }, [selected, form]);

  const toDayjsISO = (d?: Dayjs | null) => (d ? d.toISOString() : undefined);
  const toNum = (v: any): number | undefined =>
    v === "" || v === null || v === undefined ? undefined : Number(v);

  // remove undefined keys recursively so the API doesn’t drop entire sub-objects
  const prune = <T extends Record<string, any>>(obj: T): T => {
    const out: any = Array.isArray(obj) ? [] : {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        const pv = prune(v as any);
        if (Object.keys(pv).length) out[k] = pv;
      } else if (Array.isArray(v)) {
        out[k] = v.map((x) => (x && typeof x === "object" ? prune(x) : x));
      } else if (v !== undefined) {
        out[k] = v;
      }
    });
    return out;
  };

  const normalizeParent = (p: ParentInfoForm | undefined) => {
    if (!p) return undefined as any;
    return prune({
      ...p,
      // keep strings (e.g., nationalId/phone) as strings to avoid losing leading zeros
      salary: toNum(p.salary),
      income: toNum(p.income),
      nationality: p.nationality || "سوري",
      expectedFamilyExpenses: toNum(p.expectedFamilyExpenses),
      // familyBookNumber: toNum(p.familyBookNumber),
      birthdate: toDayjsISO(p.birthdate) as unknown as Date,
      // only include jobTitle/workplace when present to avoid overwriting with ""
      ...(p.jobTitle ? { jobTitle: p.jobTitle } : {}),
      ...(p.workplace ? { workplace: p.workplace } : {}),
    });
  };

  const normalizeChild = (c: ChildInfoForm) =>
    prune({
      ...c,
      salary: toNum(c.salary),
      income: toNum(c.income),
      expectedExpenses: toNum(c.expectedExpenses),
      birthdate: toDayjsISO(c.birthdate) as unknown as Date,
    });

  const normalizeCar = (c: CarInfoForm) =>
    prune({
      ...c,
      year: toNum(c.year),
      estimatedValue: toNum(c.estimatedValue),
      registrationDate: toDayjsISO(c.registrationDate) as unknown as Date,
    });

  const normalizeFarmland = (f: IFarmlandInfo) =>
    prune({
      ...f,
      areaHectares: f.areaHectares?.toString(),
      estimatedValue: toNum(f.estimatedValue),
      expectedAnnualIncome: toNum(f.expectedAnnualIncome),
    });

  const onFinish = async (values: FamilyFormValues) => {
    try {
      const payload: Partial<IFamily> = prune({
        father: normalizeParent(values.father),
        mother: prune({
          ...normalizeParent(values.mother),
          // IMPORTANT: use mother's own expectedFamilyExpenses (not father's)
          expectedFamilyExpenses: toNum(values.mother?.expectedFamilyExpenses),
          // only set village if you intentionally mirror father’s village
          // village: values.father?.village,
        }),
        children: Array.isArray(values.children)
          ? values.children.map(normalizeChild)
          : undefined,
        cars: Array.isArray(values.cars)
          ? values.cars.map(normalizeCar)
          : undefined,
        farmlands: Array.isArray(values.farmlands)
          ? values.farmlands.map(normalizeFarmland)
          : undefined,
      });

      console.log("payload", payload);

      if (id) {
        // don't await mutate; navigate on success only
        updateFamily(
          //@ts-ignore
          { _id: id, ...payload },
          {
            onSuccess: () => {
              message.success("تم تحديث بيانات العائلة بنجاح ✅");
              navigate("/");
            },
            onError: (err: any) => {
              const errorMsg =
                err?.response?.data?.message?.[0] ||
                err?.message ||
                "حدث خطأ غير متوقع، يرجى المحاولة لاحقًا";
              message.error(errorMsg);
            },
          }
        );
      } else {
        createFamily(payload as IFamily, {
          onSuccess: () => {
            message.success("تمت إضافة العائلة بنجاح ✅");
            navigate("/");
          },
          onError: (err: any) => {
            const errorMsg =
              err?.response?.data?.message?.[0] ||
              err?.message ||
              "حدث خطأ غير متوقع، يرجى المحاولة لاحقًا";
            message.error(errorMsg);
          },
        });
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message?.[0] ||
        err?.message ||
        "حدث خطأ غير متوقع، يرجى المحاولة لاحقًا";
      message.error(errorMsg);
    }
  };

  useEffect(() => {
    if (createSucceeded || updateSucceeded) {
      onClose();
    }
  }, [createSucceeded, updateSucceeded]);

  const [modalWidth, setModalWidth] = useState("90%");

  useEffect(() => {
    const updateWidth = () => {
      const w = window.innerWidth;
      if (w < 640) setModalWidth("98%"); // mobile
      else if (w < 1024) setModalWidth("90%"); // tablet
      else if (w < 1440) setModalWidth("95%"); // laptop
      else setModalWidth("90%"); // desktop
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={modalWidth}
      style={{ top: 10, padding: 0 }}
      bodyStyle={{
        height: "calc(100vh - 100px)",
        overflowY: "auto",
        padding: "0.5rem",
        paddingRight: "0rem",
        paddingLeft: "0rem",
        fontSize: "1rem", // ~16px
        lineHeight: 1.6,
      }}
      destroyOnClose
      className="rounded-xl"
    >
      <FamilyFormStepper
        onClose={onClose}
        onSubmit={onFinish}
        selected={selected as IFamily}
        rtl={rtl}
        isLoading={createLoading || updateLoading}
      />
    </Modal>
  );
}
